import { useState } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { Menu, Search, Send, ShieldCheck, X, User, LogIn } from "lucide-react";
import { useCategories } from "@/context/CategoriesContext";
import { useSettings } from "@/context/SettingsContext";
import { useAppAuth } from "@/context/AppAuthContext";
import { formatDate } from "@/lib/format";
import {
  Sheet,
  SheetContent,
  SheetTrigger,
  SheetClose,
} from "@/components/ui/sheet";
import { cn } from "@/lib/utils";

function Logo({ title }) {
  const words = (title || "Подуяне Днес").split(" ");
  const last = words.pop();
  const first = words.join(" ");
  return (
    <span className="font-display text-xl font-extrabold leading-none tracking-tight text-slate-900 sm:text-2xl">
      {first && <>{first.toUpperCase()} </>}
      <span className="text-primary">{last.toUpperCase()}</span>
    </span>
  );
}

function AccountLink() {
  const { user } = useAppAuth();
  if (user) {
    return (
      <Link to="/profil" data-testid="header-profile-link" className="inline-flex items-center gap-1 font-medium text-slate-600 hover:text-primary">
        <User className="h-3.5 w-3.5" />
        {(user.name || "Профил").split(" ")[0]}
      </Link>
    );
  }
  return (
    <Link to="/vhod" data-testid="header-login-link" className="inline-flex items-center gap-1 font-medium text-slate-600 hover:text-primary">
      <LogIn className="h-3.5 w-3.5" />
      Вход
    </Link>
  );
}

export default function Header() {
  const [query, setQuery] = useState("");
  const navigate = useNavigate();
  const { categories } = useCategories();
  const settings = useSettings();

  const onSearch = (e) => {
    e.preventDefault();
    const q = query.trim();
    if (q) {
      navigate(`/category/all?search=${encodeURIComponent(q)}`);
      setQuery("");
    }
  };

  const navClass = ({ isActive }) =>
    cn(
      "relative whitespace-nowrap px-1 py-3 text-sm font-semibold transition-colors",
      isActive ? "text-primary" : "text-slate-600 hover:text-slate-900",
    );

  return (
    <header
      className="sticky top-0 z-50 border-b border-slate-200 bg-white/95 backdrop-blur-md"
      data-testid="site-header"
    >
      <div className="hidden border-b border-slate-100 bg-slate-50 md:block">
        <div className="mx-auto flex max-w-7xl items-center justify-between px-4 py-1.5 text-xs text-slate-500 sm:px-6 lg:px-8">
          <span data-testid="header-date">
            {formatDate(new Date().toISOString())} · София, район Подуяне
          </span>
          <div className="flex items-center gap-4">
            <Link to="/za-nas" className="hover:text-slate-900">За нас</Link>
            <Link to="/kontakti" className="hover:text-slate-900">Контакти</Link>
            <AccountLink />
            <Link
              to="/admin/login"
              data-testid="header-admin-link"
              className="inline-flex items-center gap-1 font-medium text-slate-600 hover:text-primary"
            >
              <ShieldCheck className="h-3.5 w-3.5" />
              Редактори
            </Link>
          </div>
        </div>
      </div>

      <div className="mx-auto flex max-w-7xl items-center gap-4 px-4 py-3 sm:px-6 lg:px-8">
        <MobileMenu categories={categories} title={settings.site_title} />

        <Link to="/" className="flex items-center gap-2" data-testid="header-logo">
          <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-primary font-display text-lg font-extrabold text-white">
            {(settings.site_title || "П").charAt(0)}
          </span>
          <Logo title={settings.site_title} />
        </Link>

        <div className="ml-auto flex items-center gap-2">
          <form
            onSubmit={onSearch}
            className="hidden items-center rounded-full border border-slate-200 bg-slate-50 px-3 py-1.5 focus-within:border-primary lg:flex"
          >
            <Search className="h-4 w-4 text-slate-400" />
            <input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Търсене..."
              data-testid="header-search-input"
              className="w-40 bg-transparent px-2 text-sm text-slate-700 outline-none placeholder:text-slate-400"
            />
          </form>

          <Link
            to="/izprati-signal"
            data-testid="header-submit-signal-btn"
            className="inline-flex items-center gap-1.5 rounded-full bg-primary px-3.5 py-2 text-sm font-semibold text-white shadow-sm transition-colors hover:bg-[#0041A8]"
          >
            <Send className="h-4 w-4" />
            <span className="hidden sm:inline">Изпрати сигнал</span>
            <span className="sm:hidden">Сигнал</span>
          </Link>
        </div>
      </div>

      <nav className="border-t border-slate-100">
        <div className="mx-auto flex max-w-7xl items-center gap-6 overflow-x-auto px-4 sm:px-6 lg:px-8">
          <NavLink to="/" end className={navClass} data-testid="nav-home">
            Начало
          </NavLink>
          {categories.map((cat) => (
            <NavLink
              key={cat.id || cat.slug}
              to={`/category/${cat.slug}`}
              className={navClass}
              data-testid={`nav-${cat.slug}`}
            >
              {cat.name}
            </NavLink>
          ))}
          <NavLink to="/obyavi" className={navClass} data-testid="nav-obyavi">
            Обяви
          </NavLink>
        </div>
      </nav>
    </header>
  );
}

function MobileMenu({ categories, title }) {
  const [open, setOpen] = useState(false);
  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetTrigger asChild>
        <button
          className="rounded-md p-1.5 text-slate-700 hover:bg-slate-100 md:hidden"
          data-testid="mobile-menu-trigger"
          aria-label="Меню"
        >
          <Menu className="h-6 w-6" />
        </button>
      </SheetTrigger>
      <SheetContent side="left" className="w-72 p-0">
        <div className="flex items-center justify-between border-b border-slate-100 p-4">
          <span className="font-display text-lg font-extrabold text-slate-900">
            {(title || "Подуяне Днес").toUpperCase()}
          </span>
          <SheetClose className="rounded-md p-1 hover:bg-slate-100">
            <X className="h-5 w-5" />
          </SheetClose>
        </div>
        <div className="flex flex-col p-2">
          <MobileLink to="/" label="Начало" onNav={() => setOpen(false)} />
          {categories.map((cat) => (
            <MobileLink
              key={cat.id || cat.slug}
              to={`/category/${cat.slug}`}
              label={cat.name}
              onNav={() => setOpen(false)}
            />
          ))}
          <MobileLink to="/obyavi" label="Обяви" onNav={() => setOpen(false)} />
          <div className="my-2 border-t border-slate-100" />
          <MobileAccountLinks onNav={() => setOpen(false)} />
          <div className="my-2 border-t border-slate-100" />
          <MobileLink to="/za-nas" label="За нас" onNav={() => setOpen(false)} />
          <MobileLink to="/kontakti" label="Контакти" onNav={() => setOpen(false)} />
          <MobileLink to="/izprati-signal" label="Изпрати сигнал" onNav={() => setOpen(false)} />
          <MobileLink to="/publikuvai-obyava" label="Публикувай обява" onNav={() => setOpen(false)} />
          <MobileLink to="/admin/login" label="Редакторски портал" onNav={() => setOpen(false)} />
        </div>
      </SheetContent>
    </Sheet>
  );
}

function MobileAccountLinks({ onNav }) {
  const { user } = useAppAuth();
  if (user) {
    return <MobileLink to="/profil" label="Моят профил" onNav={onNav} />;
  }
  return (
    <>
      <MobileLink to="/vhod" label="Вход" onNav={onNav} />
      <MobileLink to="/registraciya" label="Регистрация" onNav={onNav} />
    </>
  );
}

function MobileLink({ to, label, onNav }) {
  return (
    <NavLink
      to={to}
      end={to === "/"}
      onClick={onNav}
      className={({ isActive }) =>
        cn(
          "rounded-lg px-3 py-2.5 text-sm font-semibold",
          isActive ? "bg-primary/10 text-primary" : "text-slate-700 hover:bg-slate-100",
        )
      }
    >
      {label}
    </NavLink>
  );
}
